/*
 *   Copyright (c) 2021 Propally.Inc
 *   All rights reserved.
 */
import { Dayjs } from "dayjs";
import dayjs = require("dayjs");
import _ = require("lodash");
import { selector, selectorFamily } from "recoil";

import { MonthlyCashflowResult } from "../api/generated/api";
import { MonthlyCashflowResultTemplate } from "../logics/monthly_cashflow_result_template";

import {
  ComparisonTargetMonth,
  PropertyPriceSimulationState,
  PropertySellSimulationState,
  SelectedEndingMonth,
  SelectedPropertyIds,
  SelectedStartingMonth,
  UserState,
} from "./atoms";
import { Properties, SelectedProperties } from "./property_selectors";

export type CashflowResultWithLatestResult = MonthlyCashflowResult & {
  property_tax_of_last_year?: number;
  fire_insurance_payment_at?: Dayjs;
  fire_insurance_holding_period_years?: number;
  fire_insurance_payment_price?: number;
  fire_insurance_distributed_price?: number;
  earthquake_insurance_payment_at?: Dayjs;
  earthquake_insurance_holding_period_years?: number;
  earthquake_insurance_payment_price?: number;
  earthquake_insurance_distributed_price?: number;
  already_paid_loan_amount?: number;
  fixed_loan_payment_amount?: number;
  remaining_loan_payment_period?: number;
  monthly_building_depreciation?: number;
  monthly_facilities_depreciation?: number;
  real_estate_income_of_last_year?: number;
  real_estate_income_of_jan_and_feb?: number;
  real_estate_income_before_deduction_of_blue_returns?: number;
};

export interface MonthlyBalanceSheet {
  month: Dayjs;
  result: CashflowResultWithLatestResult;
  price_of_properties: number;
  price_of_loan: number;
  property_acquisition_tax: number;
  price_of_misc_expences_at_bought: number;
  remaining_loan_amount: number;
  simulated_sales_price: number;
  cumulative_rent_income: number;
  cumulative_misc_income: number;
  cumulative_expence: number;
  cumulative_misc_expence: number;
  cumulative_management_cost: number;
  cumulative_repair_fund_cost: number;
  cumulative_bulding_management_cost: number;
  cumulative_misc_cost: number;
  cumulative_loan_payment: number;
  cumulative_property_acquisition_tax: number;
  cumulative_expences_at_buying: number;
  cumulative_misc_expences_at_buying: number;
  cumulative_outsourcing_fee_of_new_lease_contract: number;
  cumulative_outsourcing_fee_of_updating_lease_contract: number;
  cumulative_monthly_building_depreciation: number;
  cumulative_monthly_facilities_depreciation: number;
  selling_agent_fee: number;
  stamp_tax: number;
  misc_selling_expense: number;
  capital_gain_tax: number;
  sales_amount_by_selling: number;
  loan_prepayment_by_selling: number;
  expenses_by_selling: number;
  cumulative_sales_amount_by_selling: number;
  cumulative_loan_prepayment_by_selling: number;
  cumulative_expenses_by_selling: number;
  cumulative_real_estate_income_taxes: number;
}

export const MonthlyBalanceSheetPrototype: MonthlyBalanceSheet = {
  month: dayjs(),
  result: _.cloneDeep(MonthlyCashflowResultTemplate),
  price_of_properties: 0,
  price_of_loan: 0,
  property_acquisition_tax: 0,
  price_of_misc_expences_at_bought: 0,
  remaining_loan_amount: 0,
  simulated_sales_price: 0,
  cumulative_rent_income: 0,
  cumulative_misc_income: 0,
  cumulative_expence: 0,
  cumulative_misc_expence: 0,
  cumulative_repair_fund_cost: 0,
  cumulative_management_cost: 0,
  cumulative_bulding_management_cost: 0,
  cumulative_misc_cost: 0,
  cumulative_loan_payment: 0,
  cumulative_property_acquisition_tax: 0,
  cumulative_expences_at_buying: 0,
  cumulative_misc_expences_at_buying: 0,
  cumulative_outsourcing_fee_of_new_lease_contract: 0,
  cumulative_outsourcing_fee_of_updating_lease_contract: 0,
  cumulative_monthly_building_depreciation: 0,
  cumulative_monthly_facilities_depreciation: 0,
  selling_agent_fee: 0,
  stamp_tax: 0,
  misc_selling_expense: 0,
  capital_gain_tax: 0,
  sales_amount_by_selling: 0,
  loan_prepayment_by_selling: 0,
  expenses_by_selling: 0,
  cumulative_sales_amount_by_selling: 0,
  cumulative_loan_prepayment_by_selling: 0,
  cumulative_expenses_by_selling: 0,
  cumulative_real_estate_income_taxes: 0,
};

export const PropertyIdToThisMonthlyBalanceSheet = selectorFamily<Map<number, MonthlyBalanceSheet>, null>({
  key: "property_id_to_this_monthly_balance_sheet",
  get:
    () =>
    async ({ get }) => {
      const this_month = dayjs().format("YYYY/MM");
      return get(Properties).reduce((map, property) => {
        const monthly_balance_sheets = get(MonthlyBalanceSheets(property.id));
        const this_monthly_balance_sheet = monthly_balance_sheets.get(this_month);
        // {key: 物件ID, value: 今月分balance_sheet} のMapで返却
        map.set(property.id, this_monthly_balance_sheet);
        return map;
      }, new Map<number, MonthlyBalanceSheet>());
    },
});

export const MonthlyBalanceSheets = selectorFamily<Map<string, MonthlyBalanceSheet>, number>({
  key: "monthly_balance_sheets",
  get:
    (property_id) =>
    async ({ get }) => {
      const user = get(UserState);
      const target_properties = get(Properties);

      const property = target_properties.find((property) => {
        return property.id === property_id;
      });
      if (property === undefined) {
        throw new Error(`There is no property data with the id: ${property_id}`);
      }
      const sell_simulation_state = get(PropertySellSimulationState(property.id));
      const price_simulation_state = get(PropertyPriceSimulationState(property.id));

      let worker_end = false;
      let result: Map<string, MonthlyBalanceSheet>;

      const worker = new Worker("../logics/calculate_cumulative_balance_sheet.ts");
      worker.onmessage = (e) => {
        result = e.data;
        worker_end = true;
      };

      const promise: Promise<Map<string, MonthlyBalanceSheet>> = new Promise((resolve) => {
        const timer = setInterval(() => {
          if (worker_end) {
            worker.terminate();
            resolve(result);
            clearInterval(timer);
          }
        }, 50);
      });

      worker.postMessage({
        user: user,
        property: property,
        property_sell_simulation_state: sell_simulation_state,
        property_price_simulation_state: price_simulation_state,
      });
      await promise;

      return promise;
    },
});

// 全ての年の月ごとの結果Map. イメージ) 1960/01: MonthlyBalanceSheet, 1960/02: MonthlyBalanceSheet, ..., 2049/12: MonthlyBalanceSheet
export const ReducedMonthlyBalanceSheets = selectorFamily<Map<string, MonthlyBalanceSheet>, null>({
  key: "reduced_monthly_results",
  get:
    () =>
    async ({ get }) => {
      const property_ids = get(SelectedPropertyIds);
      // property_resultsは物件ごとに配列で結果Mapを保持.  イメージ) property_results[0] = 物件Aの月ごとの全ての年の結果Map, property_results[1] = 物件Bの月ごとの全ての年の結果Map, ...
      const property_results = property_ids.map((property_id) => {
        // 1物件の全ての年の月ごとの結果Map
        return get(MonthlyBalanceSheets(property_id));
      });

      let worker_end = false;
      let result: Map<string, MonthlyBalanceSheet>;

      if (window.Worker === undefined) throw Error("このブラウザはWebWorkerをサポートしていません。");

      const worker = new Worker("../logics/calculate_reduced_monthly_cashflow_result.ts");
      worker.onmessage = (e) => {
        result = e.data;
        worker_end = true;
      };

      const promise: Promise<Map<string, MonthlyBalanceSheet>> = new Promise((resolve) => {
        const timer = setInterval(() => {
          if (worker_end) {
            worker.terminate();
            resolve(result);
            clearInterval(timer);
          }
        }, 50);
      });

      worker.postMessage({
        property_results: property_results,
      });
      await promise;

      return promise;
    },
});

// 選択された年の1月~12月の結果配列
export const MonthlyCashflowResultsOnSelectedPeriod = selector({
  key: "monthly_results_on_selected_period",
  get: ({ get }): Array<MonthlyBalanceSheet> => {
    const start_at = get(SelectedStartingMonth);
    const end_at = get(SelectedEndingMonth);

    // 全体取得
    const whole_results = get(ReducedMonthlyBalanceSheets(null));
    const results: MonthlyBalanceSheet[] = [];

    // 全体から選択された年に絞っている
    whole_results.forEach((r, month) => {
      const is_in_the_period =
        ((start_at.isBefore(dayjs(month)) || start_at.isSame(dayjs(month))) && end_at.isAfter(dayjs(month))) ||
        end_at.isSame(dayjs(month));

      if (is_in_the_period) results.push(r);
    });
    return results;
  },
});

export const MonthlyCashflowResultsOnSelectedPeriodWithYearlySum = selector({
  key: "monthly_results_on_selected_period_with_yearly_sim",
  get: ({ get }): Array<MonthlyBalanceSheet> => {
    // 1月~12月分配列取得
    const monthly = get(MonthlyCashflowResultsOnSelectedPeriod);
    const results = _.cloneDeep(monthly);
    if (results.length === 0) return [];

    // 年合計格納用変数
    const yearly = _.cloneDeep(MonthlyBalanceSheetPrototype);

    const excludeKeys = [
      "cumulative",
      "remaining_loan_amount",
      "simulated",
      "selling_agent_fee",
      "stamp_tax",
      "misc_selling_expense",
    ];

    // 最後尾に年合計を追加
    monthly.forEach((r) => {
      if (r === monthly[-1]) return;

      Object.keys(yearly).forEach((key) => {
        if (excludeKeys.some((k) => key.includes(k))) return;

        if (key === "result") {
          Object.keys(yearly.result).forEach((k) => {
            if (!yearly.result[k]) yearly.result[k] = 0;
            if (k !== "month") yearly.result[k] += r.result[k];
          });
        } else if (key !== "month") {
          yearly[key] += r[key];
        }
      });
    });
    results.push(yearly);

    return results;
  },
});

export const YearlyCashflowResultsOnSelectedPeriod = selector({
  key: "yearly_results_on_selected_period",
  get: ({ get }): Array<MonthlyBalanceSheet> => {
    const start_at = get(SelectedStartingMonth).startOf("year");
    const end_at = get(SelectedEndingMonth).endOf("year");

    const whole_results = get(ReducedMonthlyBalanceSheets(null));
    const results: MonthlyBalanceSheet[] = [];

    whole_results.forEach((r, month) => {
      const dmonth = dayjs(month);
      const is_in_the_period =
        ((start_at.isBefore(dmonth) || start_at.isSame(dmonth)) && end_at.isAfter(dmonth)) || end_at.isSame(dmonth);
      const is_the_beginning_of_this_year = dmonth.month() === 0;

      if (is_in_the_period) {
        if (is_the_beginning_of_this_year) {
          const result = _.cloneDeep(r);
          result.month = dmonth;
          results.push(result);
        } else {
          const saved = results[results.length - 1];
          for (const key in saved) {
            if (key === "result") {
              for (const k in saved.result) {
                if (k !== "month") saved.result[k] += r.result[k];
              }
            } else if (key !== "month") {
              saved[key] += r[key];
            }
          }
        }
      }
    });
    return results;
  },
});

export const MonthlyCashflowResultsOnToday = selector({
  key: "monthly_results_on_today",
  get: ({ get }): MonthlyBalanceSheet => {
    const this_month = dayjs().startOf("month").format("YYYY/MM");
    const whole_results = get(ReducedMonthlyBalanceSheets(null));

    return whole_results.get(this_month);
  },
});

export const LoanInformationOnToday = selector({
  key: "loan_information_on_today",
  get: ({ get }) => {
    const this_month = dayjs().startOf("month").format("YYYY/MM");
    const property_ids = get(SelectedPropertyIds);
    const target_properties = get(Properties);

    const response = {
      number_of_loan_contracts: 0,
      total_amount: 0,
      monthly_payment_amount: 0,
      total_loan_remaining: 0,
      properties: [],
    };

    property_ids.forEach((property_id) => {
      const results = get(MonthlyBalanceSheets(property_id));
      const this_month_result = results.get(this_month);
      if (this_month_result.remaining_loan_amount > 0) {
        response.number_of_loan_contracts++;
        const property = target_properties.find((p) => {
          return p.id === property_id;
        });

        response.total_amount += property.property.loan_amount;
        response.monthly_payment_amount +=
          this_month_result.result.loan_interest +
          this_month_result.result.loan_principal +
          this_month_result.result.loan_prepayment;
        response.total_loan_remaining += this_month_result.remaining_loan_amount;

        // 物件ごとの情報を追加
        response.properties.push({
          id: property_id,
          name: property.property.name,
          remaining_loan_amount: this_month_result.remaining_loan_amount,
        });
      }
    });

    return response;
  },
});

export const MonthlyBalanceSheetsOnComparisonTargetMonth = selector({
  key: "MonthlyBalanceSheetsOnComparisonTargetMonth",
  get: ({ get }) => {
    const comparisonTargetMonth = get(ComparisonTargetMonth);
    const properties = get(SelectedProperties);
    return properties.map((property) => {
      return {
        property: property,
        result: get(MonthlyBalanceSheets(property.id)).get(comparisonTargetMonth.format("YYYY/MM")),
      };
    });
  },
});
